/** El libro en Google Sheets, leído directamente desde el dispositivo con el token del usuario (drive.file). */
import { DATA_RANGES, FORMULA_RANGES, FORMULAS, OPTIONAL_RANGES, type Values } from '@pf/workbook';
import type { RangeKey, WorkbookSource } from '../app/ports.js';

type Token = () => Promise<string>;

async function google<T>(url: string, token: Token): Promise<T> {
  const r = await fetch(url, { headers: { authorization: `Bearer ${await token()}` } });
  if (!r.ok) {
    const j = await r.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(r.status === 404 ? 'La app ya no tiene acceso al libro (¿lo has borrado o movido?)' : `Google: ${j.error?.message ?? r.status}`);
  }
  return r.json() as Promise<T>;
}

const colNum = (letters: string) => [...letters].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0);
const colLetter = (n: number): string => { let s = ''; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; };

/** Celdas devueltas por un rango en modo FORMULA → { 'Pestaña!J1': '=…' } (solo las del esquema). */
export function formulaCells(range: string, values: Values): Record<string, string> {
  const m = range.match(/^'?(.+?)'?!([A-Z]+)(\d+)/);
  if (!m) return {};
  const [, tab, col, row] = m;
  const out: Record<string, string> = {};
  values.forEach((r, i) => r.forEach((v, j) => {
    const cell = `${tab}!${colLetter(colNum(col!) + j)}${Number(row) + i}`;
    if (cell in FORMULAS && typeof v === 'string') out[cell] = v;
  }));
  return out;
}

export function sheetsSource(fileId: string, token: Token): WorkbookSource {
  const base = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(fileId)}`;
  const batch = (ranges: readonly string[], render: string) => google<{ valueRanges: Array<{ range: string; values?: Values }> }>(
    `${base}/values:batchGet?${new URLSearchParams([...ranges.map(r => ['ranges', r]), ['valueRenderOption', render], ['dateTimeRenderOption', 'SERIAL_NUMBER']])}`, token);
  let meta: Promise<{ name: string; modifiedTime: string }> | null = null;
  const driveMeta = () => google<{ name: string; modifiedTime: string }>(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=name,modifiedTime`, token);
  return {
    id: fileId,
    title: async () => (await (meta ??= driveMeta())).name,
    modifiedTime: async () => { meta = driveMeta(); return (await meta).modifiedTime; },
    async readData() {
      const keys = Object.keys(DATA_RANGES) as RangeKey[];
      // Pestañas opcionales aparte: si una no existe, Google rechaza todo el lote; así solo se marca como ausente
      const optional = (Object.keys(OPTIONAL_RANGES) as Array<keyof typeof OPTIONAL_RANGES>);
      const [data, formulas, extra] = await Promise.all([batch(keys.map(k => DATA_RANGES[k]), 'UNFORMATTED_VALUE'), batch(FORMULA_RANGES, 'FORMULA'),
        Promise.all(optional.map(k => batch([OPTIONAL_RANGES[k]], 'UNFORMATTED_VALUE').then(r => r.valueRanges[0]?.values ?? [], () => null)))]);
      const ranges = Object.fromEntries([...keys.map((k, i) => [k, data.valueRanges[i]?.values ?? []]), ...optional.map((k, i) => [k, extra[i]])]) as Record<RangeKey, Values>;
      return { ranges, formulas: Object.assign({}, ...formulas.valueRanges.map(v => formulaCells(v.range, v.values ?? []))) };
    },
    async readArchive() {
      const r = await google<{ values?: Values }>(`${base}/values/${encodeURIComponent('Archivo!A1:L')}?valueRenderOption=UNFORMATTED_VALUE&dateTimeRenderOption=SERIAL_NUMBER`, token);
      return r.values ?? [];
    },
  };
}
